import { lazy, Suspense } from "react";
import ErrorBoundary from "@/components/ErrorBoundary";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Layout } from "@/components/layout";
import { AuthProvider } from "@/contexts/AuthContext";
import { ProtectedRoute } from "@/components/admin/ProtectedRoute";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { LanguageWrapper, RootEnglishWrapper, EnglishRedirect } from "@/components/LanguageWrapper";
import { PUBLIC_PAGES } from "@/lib/seo";

// Eager-load HomePage for LCP
import HomePage from "@/pages/HomePage";

// Lazy-load page components indexed by path
const pageComponents: Record<string, React.LazyExoticComponent<React.ComponentType>> = {
  "government-solutions": lazy(() => import("@/pages/GovernmentSolutionsPage")),
  "technology-platform": lazy(() => import("@/pages/TechnologyPlatformPage")),
  "operational-delivery": lazy(() => import("@/pages/OperationalDeliveryPage")),
  "global-presence": lazy(() => import("@/pages/GlobalPresencePage")),
  "tourism-trade-support": lazy(() => import("@/pages/TourismTradePage")),
  "case-studies": lazy(() => import("@/pages/CaseStudiesPage")),
  "resources": lazy(() => import("@/pages/ResourcesPage")),
  "about": lazy(() => import("@/pages/AboutPage")),
  "contact": lazy(() => import("@/pages/ContactPage")),
  "faq": lazy(() => import("@/pages/FAQPage")),
  "security": lazy(() => import("@/pages/SecurityPage")),
  "procurement": lazy(() => import("@/pages/ProcurementPage")),
  "governance": lazy(() => import("@/pages/GovernancePage")),
  "implementation": lazy(() => import("@/pages/ImplementationPage")),
  "data-privacy": lazy(() => import("@/pages/DataPrivacyPage")),
  "legal-notice": lazy(() => import("@/pages/LegalNoticePage")),
  "privacy-policy": lazy(() => import("@/pages/PrivacyPolicyPage")),
  "terms": lazy(() => import("@/pages/TermsPage")),
};

const NotFoundPage = lazy(() => import("@/pages/NotFoundPage"));

// Lazy-load admin pages
const AdminLoginPage = lazy(() => import("@/pages/admin/AdminLoginPage"));
const UnauthorizedPage = lazy(() => import("@/pages/admin/UnauthorizedPage"));
const AdminDashboardPage = lazy(() => import("@/pages/admin/AdminDashboardPage"));
const AdminLeadsPage = lazy(() => import("@/pages/admin/AdminLeadsPage"));
const AdminCaseStudiesPage = lazy(() => import("@/pages/admin/AdminCaseStudiesPage"));
const AdminResourcesPage = lazy(() => import("@/pages/admin/AdminResourcesPage"));
const AdminAuditLogPage = lazy(() => import("@/pages/admin/AdminAuditLogPage"));
const AdminSettingsPage = lazy(() => import("@/pages/admin/AdminSettingsPage"));

// Minimal loading fallback (invisible to prevent layout shift)
const PageLoader = () => null;

const queryClient = new QueryClient();

// Build route elements from centralized PUBLIC_PAGES registry (skip home, handled separately)
const publicRoutes = PUBLIC_PAGES
  .filter((p) => p.path !== "")
  .map(({ path }) => {
    const Component = pageComponents[path];
    if (!Component) return null;
    return {
      path,
      element: <Suspense fallback={<PageLoader />}><Component /></Suspense>,
    };
  })
  .filter(Boolean) as Array<{ path: string; element: JSX.Element }>;

const App = () => (
  <ErrorBoundary>
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <Sonner />
        <BrowserRouter>
          <Routes>
            {/* English routes - serve from root WITHOUT /en/ prefix */}
            <Route element={<RootEnglishWrapper><Layout /></RootEnglishWrapper>}>
              <Route index element={<HomePage />} />
              {publicRoutes.map(({ path, element }) => (
                <Route key={path} path={path} element={element} />
              ))}
              <Route path="*" element={<Suspense fallback={<PageLoader />}><NotFoundPage /></Suspense>} />
            </Route>

            {/* Redirect /en/* to /* (root) */}
            <Route path="/en" element={<Navigate to="/" replace />} />
            <Route path="/en/*" element={<EnglishRedirect />} />
            
            {/* Non-English language routes (19 other languages) */}
            <Route path="/:lang" element={<LanguageWrapper><Layout /></LanguageWrapper>}>
              <Route index element={<HomePage />} />
              {publicRoutes.map(({ path, element }) => (
                <Route key={`lang-${path}`} path={path} element={element} />
              ))}
              <Route path="*" element={<Suspense fallback={<PageLoader />}><NotFoundPage /></Suspense>} />
            </Route>

            {/* Admin Auth Routes (no layout, no language prefix) */}
            <Route path="/admin/login" element={<Suspense fallback={<PageLoader />}><AdminLoginPage /></Suspense>} />
            <Route path="/admin/unauthorized" element={<Suspense fallback={<PageLoader />}><UnauthorizedPage /></Suspense>} />

            {/* Protected Admin Routes */}
            <Route
              element={
                <ProtectedRoute>
                  <AdminLayout />
                </ProtectedRoute>
              }
            >
              <Route path="/admin" element={<Suspense fallback={<PageLoader />}><AdminDashboardPage /></Suspense>} />
              <Route path="/admin/leads" element={<Suspense fallback={<PageLoader />}><AdminLeadsPage /></Suspense>} />
              <Route path="/admin/case-studies" element={<Suspense fallback={<PageLoader />}><AdminCaseStudiesPage /></Suspense>} />
              <Route path="/admin/resources" element={<Suspense fallback={<PageLoader />}><AdminResourcesPage /></Suspense>} />
              <Route path="/admin/audit-log" element={<Suspense fallback={<PageLoader />}><AdminAuditLogPage /></Suspense>} />
              <Route path="/admin/settings" element={<Suspense fallback={<PageLoader />}><AdminSettingsPage /></Suspense>} />
            </Route>
          </Routes>
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
